import { resolveAppTheme } from "@token/shared";
import { resolveBranding } from "../../branding";
import { PaymentProviderName, type AppConfig, type ConfigSource } from "../types";
import { parsePaymentEnvironment, parsePaymentProvider } from "./env";

type RawPortalConfig = Omit<AppConfig, "payment" | "theme" | "branding"> & {
  payment?: {
    environment?: unknown;
    provider?: unknown;
  };
  theme?: unknown;
  branding?: unknown;
};

export interface BackendConfigOptions {
  url: string;
  fetchImpl?: typeof fetch;
}

export class BackendConfigSource implements ConfigSource {
  readonly name = "backend";
  private readonly url: string;
  private readonly fetchImpl: typeof fetch;

  constructor(opts: BackendConfigOptions) {
    this.url = opts.url;
    this.fetchImpl = opts.fetchImpl ?? fetch.bind(globalThis);
  }

  async load(): Promise<AppConfig> {
    const response = await this.fetchImpl(this.url, {
      credentials: "include",
      headers: { Accept: "application/json" },
    });
    if (!response.ok) {
      throw new Error(`Config request failed: ${response.status} ${response.statusText}`);
    }
    const config = (await response.json()) as RawPortalConfig;
    assertValidConfig(config);
    const paymentProvider =
      parseBackendPaymentProvider(config.payment?.provider) ??
      parsePaymentProvider(import.meta.env.VITE_PORTAL_PAYMENT_PROVIDER) ??
      PaymentProviderName.Mock;
    const paymentEnvironment =
      parseBackendPaymentEnvironment(config.payment?.environment) ??
      parsePaymentEnvironment(import.meta.env.VITE_PORTAL_ADYEN_ENVIRONMENT);

    return {
      ...config,
      payment: {
        provider: paymentProvider,
        ...(paymentEnvironment ? { environment: paymentEnvironment } : {}),
      },
      theme: parseBackendTheme(config.theme),
      branding: resolveBranding(config.branding),
    };
  }
}

function assertValidConfig(data: unknown): asserts data is RawPortalConfig {
  if (!data || typeof data !== "object") {
    throw new Error("Config response is not an object");
  }
  const auth = (data as { auth?: unknown }).auth;
  if (!auth || typeof auth !== "object") {
    throw new Error("Config response missing 'auth'");
  }
  const providers = (auth as { providers?: unknown }).providers;
  if (!Array.isArray(providers)) {
    throw new Error("Config response auth.providers is not an array");
  }
  providers.forEach((item) => {
    if (item !== "mock" && item !== "demo" && item !== "google" && item !== "auth0" && item !== "backend") {
      throw new Error(`Config response has unknown auth.providers item: ${String(item)}`);
    }
  });
}

function parseBackendPaymentProvider(value: unknown) {
  if (value === undefined || value === null || value === "") {
    return undefined;
  }
  if (typeof value !== "string") {
    throw new Error("Config response payment.provider is not a string");
  }
  const provider = parsePaymentProvider(value);
  if (!provider) {
    throw new Error(`Config response has unknown payment.provider: ${value}`);
  }
  return provider;
}

function parseBackendPaymentEnvironment(value: unknown) {
  if (value === undefined || value === null || value === "") {
    return undefined;
  }
  if (typeof value !== "string") {
    throw new Error("Config response payment.environment is not a string");
  }
  const environment = parsePaymentEnvironment(value);
  if (!environment) {
    throw new Error(`Config response has unknown payment.environment: ${value}`);
  }
  return environment;
}

function parseBackendTheme(value: unknown) {
  return typeof value === "string" ? resolveAppTheme(value) : resolveAppTheme(undefined);
}
